import { IRetryPolicy, RetryContext } from '@microsoft/signalr';

/** Delays for the first attempts after a drop; afterwards every attempt waits SIGNALR_MAX_RETRY_DELAY_MS. */
export const SIGNALR_RETRY_DELAYS_MS = [0, 2000, 10000, 30000];
export const SIGNALR_MAX_RETRY_DELAY_MS = 60000;
/** Spread for the immediate retry, so clients dropped together (a deploy) don't reconnect in the same instant. */
export const SIGNALR_FIRST_RETRY_SPREAD_MS = 1000;

/**
 * Errors after which retrying can't help: the OIDC adapter's "Not Logged In", and IdentityServer silent-renew
 * results meaning the session itself is over.
 */
const SESSION_OVER_MARKERS = ['Not Logged In', 'login_required', 'interaction_required'];

/**
 * Matches the message text or the OIDC error code. With skipNegotiation (our config) SignalR rethrows the token
 * error as is. The innerErrors walk is defensive: with negotiation SignalR wraps it in FailedToStartTransportError
 * (message text only) inside AggregateErrors.innerErrors.
 */
export function isLoggedOutError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const oidcCode = 'error' in error && typeof error.error === 'string' ? error.error : '';
  if (SESSION_OVER_MARKERS.some((marker) => `${error.message} ${oidcCode}`.includes(marker))) {
    return true;
  }
  return 'innerErrors' in error && Array.isArray(error.innerErrors) && error.innerErrors.some(isLoggedOutError);
}

/**
 * Retry forever with a capped backoff (the SignalR default gives up after 4 attempts, leaving live
 * notifications dead until a page reload). Stops only when the session is over. Every delay is jittered
 * (the immediate retry by up to 1s, the others by up to 20%) to spread reconnects after a deploy.
 */
export function nextSignalRRetryDelay(
  previousRetryCount: number,
  retryReason?: unknown,
  random: () => number = Math.random
): number | null {
  if (isLoggedOutError(retryReason)) {
    return null;
  }
  const base = SIGNALR_RETRY_DELAYS_MS[previousRetryCount] ?? SIGNALR_MAX_RETRY_DELAY_MS;
  return base === 0 ? Math.floor(random() * SIGNALR_FIRST_RETRY_SPREAD_MS) : base + Math.floor(random() * base * 0.2);
}

export const sgSignalRRetryPolicy: IRetryPolicy = {
  nextRetryDelayInMilliseconds: (context: RetryContext) =>
    nextSignalRRetryDelay(context.previousRetryCount, context.retryReason),
};
